import clsx from 'clsx';
import _ from 'lodash';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import InfiniteScroll from 'react-infinite-scroller';
import { SearchResultAssetIcons } from '@/assetGroupEditor/SearchResultAssetIcons.molecule';
import { ErrorWithBackground } from '@/core/ErrorWithBackground.atom';
import { IconWithSpinner } from '@/core/IconWithSpinner.atom';
import { ToggleSwitch } from '@/core/ToggleSwitch.atom';
import {
  sqItemTableEditorStore,
  sqScalingTableEditorStore,
  sqWorkbenchStore,
  sqWorkbookStore,
} from '@/core/core.stores';
import { useFlux } from '@/core/hooks/useFlux.hook';
import { useFluxPath } from '@/core/hooks/useFluxPath.hook';
import { Feature } from '@/licenseManagement/licenseManagement.constants';
import { useHasAssetGroups, useHasValidFeature } from '@/licenseManagement/licenseManagement.store';
import { CREATED_BY_SEEQ_WORKBENCH, ITEM_TABLE_TYPE, KEY_CODES, SCALING_TABLE_TYPE } from '@/main/app.constants';
import { useLayoutAssetGroupEditorVisible, useLayoutTableDefinitionEditorVisible } from '@/main/layout.store';
import { DatasourcePreferencesToggle } from '@/search/DatasourcePreferencesToggle.molecule';
import { SearchFilters } from '@/search/SearchFilters.molecule';
import { SearchGroup, type SearchGroupProps } from '@/search/SearchGroup.molecule';
import { SearchResult } from '@/search/SearchResult.molecule';
import { SearchResultIcons } from '@/search/SearchResultIcons.molecule';
import { TrendMultiSelect } from '@/search/multiSelect/TrendMultiSelect.molecule';
import {
  cancellationGroup,
  clear,
  exploreAssetSearchActions,
  fetchAssetResults,
  fetchHierarchyLayer,
  fetchItemResults,
  fetchItemUsages,
  fetchScalingTableSearchItems,
  initializeSearchActions,
  reset,
  setUpHierarchiesSearchForItemTable,
} from '@/search/search.actions';
import { HOME_BREADCRUMB, TEMPLATE_SECTION_TYPES, type TemplateSectionType } from '@/search/search.constants';
import {
  useScalingTableViewMode,
  useSearchActions,
  useSearchBreadcrumbs,
  useSearchCurrentAsset,
  useSearchCurrentHierarchyPath,
  useSearchDatasources,
  useSearchHasNextPage,
  useSearchHighlightItemId,
  useSearchIsAsyncInitialized,
  useSearchIsPaginating,
  useSearchItems,
  useSearchLocallyScoped,
  useSearchMode,
  useSearchSearching,
  useSearchSelectedHierarchy,
  useSearchSelectedItemIds,
  useSearchSelectedItemTable,
  useSearchSelectedScalingTable,
  useSearchSelectedScalingTableRow,
  useSearchWorkbooksFilter,
} from '@/search/search.store';
import { showOnlyTheseDatasources } from '@/search/search.utilities';
import {
  type ModelItemTrendProps,
  getHierarchyItemTrendProps,
  getModelItemTrendProps,
  getScalingTableItemTrendProps,
} from '@/search/searchResult.utilities.service';
import {
  exploreHierarchyLevel,
  exploreScalingTableLevel,
  filterByAssetItemType,
  filterByName,
  filterItems,
  limitAndFilterItems,
  limitFilterAndSortItems,
  sortByKeyValuesFirst,
} from '@/search/searchWidget.utilities';
import { hasScalingTableCapability } from '@/services/authorization.service';
import { isScalingTableEditorEnabled } from '@/services/systemConfiguration.utilities';
import { getItemTableTypes, getScalingTableTypes } from '@/tableDefinitionEditor/tableDefinition.utilities';
import { partitionModelLibraryTemplates } from '@/tools/models/template.utilities';
import { TRACK } from '@/track/track.constants';
import { doTrack } from '@/track/track.service';
import { addTrendItem } from '@/trendData/trend.actions';
import { API_TYPES_TO_ITEM_TYPES } from '@/trendData/trendData.constants';
import { SUPPORT_LINK } from '@/utilities/externalLinks';
import { errorToast } from '@/utilities/toast.utilities';
import {
  isAsset,
  isAssetGroup,
  isDatafile,
  isHierarchy,
  isHierarchyNode,
  isTreeBuilderEnabledForUser,
} from '@/utilities/utilities';
import {
  setIsUsingDatasourcePrefsAssetTrees,
  setUserPreferencesDisplay,
  setUserPreferencesTab,
} from '@/workbench/workbench.actions';
import { PREFERENCE_TABS } from '@/workbench/workbench.store';
import {
  fetchAssetGroups,
  fetchAssets,
  fetchItemTables,
  fetchPinnedItems,
  fetchRecentlyAccessedItems,
  fetchScalingTables,
  fetchSharedTemplates,
  fetchTemplates,
} from '@/workbook/workbook.actions';
import { LOAD_STATUS, WORKBOOK_DISPLAY } from '@/workbook/workbook.constants';
import { TableTypeEnum } from '@seeqdev/api/model/TableDefinitionOutputV1';
import { SeeqNames } from '@seeqdev/constants';
import { faHouseChimney } from '@seeqdev/icons/fa-sharp-regular-svg-icons';
import { fcUsage } from '@seeqdev/icons/seeq';
import { Button, Checkbox, Icon, TextField } from '@seeqdev/qomponents';
import type { SearchModes, SearchPanes } from '@/search/search.constants';
import type { SearchBreadcrumb, SelectedSearchItem } from '@/search/search.types';
import type { ItemSearchPreviewV1 } from '@seeqdev/api/model/ItemSearchPreviewV1';

export interface SearchWidgetSharedProps {
  /** @default [sqWorkbookStore.workbookId] */
  scopeIds?: string[];
  searchTypes: string[];
  excludeGloballyScoped?: boolean;
  showPinnedAssets?: boolean;
  showOnlyScopedAssets?: boolean;
  showOnlyConditions?: boolean;
  /** if an override workbookId is provided then we do not use the current workbookId **/
  overrideWorkbookId?: string;
  filterByWorkbook?: boolean;
  selectAllCallback?: (items: SelectedSearchItem[]) => void;
  areAllSelected?: (itemIds: SelectedSearchItem[]) => boolean;
  itemTypeToShowInOverview?: SearchModes;
  overviewLabel?: string;
  showCreateNewItem?: boolean;
  onCreateNewItem?: () => void;
  createNewItemMessage?: string;
  createNewItemLabel?: string;
  doNotDisableOnEdit?: boolean;
  /**
   * Optional filter function to further filter items in the search results.
   * Currently only applied to scaling tables (when itemTypeToShowInOverview is 'scaling-tables').
   * This is intentionally generic to allow future expansion to other item types.
   */
  additionalItemFilter?: (items: SelectedSearchItem[]) => SelectedSearchItem[];
}

export interface SearchWidgetProps extends SearchWidgetSharedProps {
  iconClasses?: string;
  restrictExploration?: boolean;
  indicateSelection?: boolean;
  selectedItemId?: string;
  onItemClick?: (item: any, mode?: SearchModes, pane?: SearchPanes) => Promise<any> | void;
  searchResultIcons?: (item: SelectedSearchItem) => Promise<React.ReactNode>;
  showOnlyResults?: boolean;
  allowAssetReplacement: boolean;
  isSelectingAsset?: boolean;
  predefinedSearchTypes?: string[];
  searchResultsClassNames?: string;
  // Used to determine whether or not to clear the modal stores on closing the search widget
  modalId?: string;
  onSelectItem?: (item: any) => void;
  isPanelSearch?: boolean;
  initialItemTable?: SelectedSearchItem;
  initialHierarchy?: SelectedSearchItem;
  initialScalingTable?: SelectedSearchItem;
}

let lastModalId: string | undefined;

export const SearchWidget = (props: SearchWidgetProps) => {
  const { setIsPaginating, setSelectAll, setScalingTableViewMode } = useSearchActions();
  const {
    searchTypes,
    iconClasses,
    scopeIds = [sqWorkbookStore.workbookId],
    excludeGloballyScoped = false,
    onItemClick = _.noop,
    allowAssetReplacement,
    searchResultIcons = defaultSearchResultIcons,
    showOnlyResults = false,
    restrictExploration = false,
    indicateSelection = false,
    selectedItemId,
    isSelectingAsset = false,
    predefinedSearchTypes,
    modalId,
    searchResultsClassNames,
    onSelectItem = _.noop,
    isPanelSearch = false,
    selectAllCallback,
    areAllSelected = () => false,
    showPinnedAssets = false,
    showOnlyScopedAssets = false,
    showOnlyConditions = false,
    overrideWorkbookId,
    filterByWorkbook,
    itemTypeToShowInOverview,
    overviewLabel,
    showCreateNewItem = false,
    onCreateNewItem,
    createNewItemMessage = '',
    createNewItemLabel = '',
    doNotDisableOnEdit = false,
    initialItemTable,
    initialHierarchy,
    initialScalingTable,
    additionalItemFilter,
  } = props;
  if (lastModalId === '') {
    lastModalId = modalId;
  }
  const pane: SearchPanes = modalId ? 'modal' : 'main';

  const { t } = useTranslation();
  const hasScalingLicense = useHasValidFeature(Feature.Scaling_Tables);
  const hasTreeBuilderLicense = useHasValidFeature(Feature.Tree_Builder);
  const hasAssetGroups = useHasAssetGroups();
  const assetGroupEditorVisible = useLayoutAssetGroupEditorVisible();
  const scalingTableEditorVisible = useLayoutTableDefinitionEditorVisible();
  const [nameFilter, setNameFilter] = useState('');
  const [scopedAssetGroups, setScopedAssetGroups] = useState<SelectedSearchItem[]>([]);
  const [scopedAssetTrees, setScopedAssetTrees] = useState<SelectedSearchItem[]>([]);
  const [scalingTableIsCsv, setScalingTableIsCsv] = useState(false);
  const [expandedTemplatesSection, setExpandedTemplatesSection] = useState<TemplateSectionType | null>(null);
  const scrollParentRef = useRef(null);
  const selectedItemIds = useSearchSelectedItemIds();
  const hasNextPage = useSearchHasNextPage();
  const mode = useSearchMode();
  const searching = useSearchSearching();
  const isPaginating = useSearchIsPaginating();
  const currentAsset = useSearchCurrentAsset();
  const workbooksFilter = useSearchWorkbooksFilter();
  const searchLocallyScoped = useSearchLocallyScoped();
  const selectedItemTable = useSearchSelectedItemTable();
  const selectedHierarchy = useSearchSelectedHierarchy();
  const currentHierarchyPath = useSearchCurrentHierarchyPath();
  const selectedScalingTable = useSearchSelectedScalingTable();
  const selectedScalingTableRow = useSearchSelectedScalingTableRow();
  const scalingTableViewMode = useScalingTableViewMode();
  const breadcrumbs = useSearchBreadcrumbs() as SearchBreadcrumb[];
  const items = useSearchItems();
  const isAsyncInitialized = useSearchIsAsyncInitialized();
  const highlightItemId = useSearchHighlightItemId();
  const datasources = useSearchDatasources();
  const hasCreatedScalingTable = useFluxPath(sqWorkbenchStore, () => sqWorkbenchStore.hasCreatedScalingTable);
  const hasCreatedItemTable = useFluxPath(sqWorkbenchStore, () => sqWorkbenchStore.hasCreatedItemTable);
  const {
    pinned = [],
    recentlyAccessed = [],
    recentlyAccessedLoadStatus,
    pinnedLoadStatus,
    assetGroups = [],
    tableDefinitions = [],
    tableDefinitionsLoadStatus,
    itemTablesLoadStatus,
    itemTables = [],
    assetGroupsLoadStatus,
    workbookId: storeWorkbookId,
    workbookDisplay,
    templates = [],
    templatesLoadStatus,
    sharedTemplates = [],
    sharedTemplatesLoadingStatus,
  } = useFlux(sqWorkbookStore);
  const workbookId = overrideWorkbookId ? overrideWorkbookId : storeWorkbookId;
  const currentUser = useFluxPath(sqWorkbenchStore, () => sqWorkbenchStore.currentUser);
  const hasAssetGroupScope = scopeIds.some((scopeId) => scopeId && scopeId !== SeeqNames.Database.Utils.EmptyGuid);
  const scalingTableId = useFluxPath(sqScalingTableEditorStore, () => sqScalingTableEditorStore.tableId);
  const itemTableId = useFluxPath(sqItemTableEditorStore, () => sqItemTableEditorStore.tableId);
  const isUsingDatasourcePrefsAssetTrees = useFluxPath(
    sqWorkbenchStore,
    () => sqWorkbenchStore.isUsingDatasourcePrefsAssetTrees,
  );
  const includeNewDatasources = useFluxPath(sqWorkbenchStore, () => sqWorkbenchStore.includeNewDatasources);
  const userDatasources = useFluxPath(sqWorkbenchStore, () => sqWorkbenchStore.userDatasources);
  const filteredDatasources = showOnlyTheseDatasources(workbookId, datasources, userDatasources, {
    includeNewDatasources,
  });
  // If we get the same number of datasources back, then we didn't filter any datasources out of the full list.
  // That means we can skip any filtering and show all datasources.
  const skipDatasourceCheck = datasources.length === filteredDatasources.length;

  const getOverviewItems = (overviewItems: SelectedSearchItem[]) => {
    if (isUsingDatasourcePrefsAssetTrees && !skipDatasourceCheck && !itemTypeToShowInOverview) {
      return overviewItems.filter((item) => {
        if (!item?.properties) return false;
        const datasourceIDProperty = item.properties.find((prop) => prop.name === SeeqNames.Properties.DatasourceId);
        return datasourceIDProperty
          ? filteredDatasources.some((ds) => ds.datasourceId === datasourceIDProperty.value)
          : false;
      });
    }

    // Apply additionalItemFilter to scaling tables if provided (e.g., filtering by model application).
    // Currently this is the only item type that uses additionalItemFilter, but the prop is intentionally
    // generic to allow future expansion to other item types (models, item-tables, etc.).
    const filteredTableDefinitions = additionalItemFilter ? additionalItemFilter(tableDefinitions) : tableDefinitions;

    if (itemTypeToShowInOverview) {
      switch (itemTypeToShowInOverview) {
        case 'pinned':
          return pinned;
        case 'recent':
          return recentlyAccessed;
        case 'asset-groups':
          return assetGroups;
        case 'scaling-tables':
          return filteredTableDefinitions;
        case 'item-tables':
          return itemTables;
        case 'models':
          return templates;
      }
    }
    return overviewItems;
  };

  // No need for this to be a state variable as computing it is simple and this ensures it is never stale (CRAB-39094)
  const currentItems: SelectedSearchItem[] = (() => {
    const overviewItems: SelectedSearchItem[] = showOnlyScopedAssets ? scopedAssetTrees : items;
    switch (mode) {
      case 'pinned':
        return pinned;
      case 'recent':
        return recentlyAccessed;
      case 'asset-groups':
        return assetGroups;
      case 'scaling-tables':
        return tableDefinitions;
      case 'item-tables':
        return itemTables;
      case 'overview': // Asset Trees or the item type in itemTypeToShowInOverview, if defined
        return getOverviewItems(overviewItems);
      default:
        return items;
    }
  })();

  const placeholderCategory: string | undefined = (() => {
    switch (mode) {
      case 'tree':
      case 'search':
        return _.get(_.last(_.reject(breadcrumbs, ['type', 'VIEW_MODE'])), 'name');
      case 'pinned':
        return 'SEARCH_DATA.PINNED';
      case 'recent':
        return 'SEARCH_DATA.RECENTLY_ACCESSED';
      case 'asset-groups':
        return 'SEARCH_DATA.ASSET_GROUPS';
      case 'scaling-tables':
        return 'SEARCH_DATA.SCALING';
      case 'item-tables':
        return 'SEARCH_DATA.ITEM_TABLES';
      case 'item-hierarchies':
        return 'SEARCH_DATA.HIERARCHY';
      case 'usages':
        return 'SEARCH_DATA.USAGES';
    }
  })();

  useEffect(() => {
    if (scalingTableId && scalingTableId !== tableDefinitions[length - 1]?.id) {
      fetchScalingTables([workbookId], sqWorkbookStore.isVantage);
    }
  }, [scalingTableId]);

  useEffect(() => {
    if (itemTableId && itemTableId !== itemTables[length - 1]?.id) {
      fetchItemTables([workbookId]);
    }
  }, [itemTableId]);

  useEffect(() => {
    hasAssetGroupScope && assetGroupsLoadStatus === LOAD_STATUS.NOT_LOADED && fetchAssetGroups(scopeIds);
    pinnedLoadStatus === LOAD_STATUS.NOT_LOADED && workbookId && fetchPinnedItems(workbookId);
    recentlyAccessedLoadStatus === LOAD_STATUS.NOT_LOADED && workbookId && fetchRecentlyAccessedItems(workbookId);
    tableDefinitionsLoadStatus === LOAD_STATUS.NOT_LOADED &&
      fetchScalingTables([workbookId], sqWorkbookStore.isVantage);
    templatesLoadStatus === LOAD_STATUS.NOT_LOADED && fetchTemplates([workbookId]);
    itemTypeToShowInOverview === 'models' &&
      sharedTemplatesLoadingStatus === LOAD_STATUS.NOT_LOADED &&
      workbookId &&
      fetchSharedTemplates(workbookId);
    itemTablesLoadStatus === LOAD_STATUS.NOT_LOADED && fetchItemTables([workbookId]);
  }, [
    workbookId,
    assetGroupsLoadStatus,
    pinnedLoadStatus,
    recentlyAccessedLoadStatus,
    tableDefinitionsLoadStatus,
    itemTablesLoadStatus,
    templatesLoadStatus,
    sharedTemplatesLoadingStatus,
    itemTypeToShowInOverview,
  ]);

  const chosenItemTable = mode === 'item-hierarchies' ? selectedItemTable : undefined;
  const showOverviewMode = mode === 'overview';
  const {
    myTemplates: filteredModelItems,
    sharedTemplatesNotOwned: sharedModelItems,
    industryTemplates: industryModelItems,
  } = partitionModelLibraryTemplates(templates, sharedTemplates, currentUser);
  const isAllUnselectable =
    assetGroupEditorVisible && currentItems.some((item) => item.hasActiveChildren || isAsset(item));

  useEffect(() => {
    if (pane === 'modal' && (modalId !== lastModalId || showOverviewMode)) {
      reset(pane);
      initializeSearchActions(
        pane,
        searchTypes,
        restrictExploration,
        scopeIds,
        excludeGloballyScoped,
        initialItemTable,
        initialHierarchy,
        initialScalingTable,
      ).catch((error) => errorToast({ httpResponseOrError: error }));
    } else if (!isAsyncInitialized) {
      // Not ideal to initiate the async fetch from a useEffect, but there are so many modals that open this widget
      // up that this seems better than the alternative of having all the modals call it when the modal opens.
      // Note that this only gets called for modals, stateSynchronizer handles the initialization for the main panel
      // to enable fast follow to work (CRAB-26634)
      initializeSearchActions(
        pane,
        searchTypes,
        restrictExploration,
        scopeIds,
        excludeGloballyScoped,
        initialItemTable,
        initialHierarchy,
        initialScalingTable,
      ).catch((error) => errorToast({ httpResponseOrError: error }));
    }

    if (showOnlyScopedAssets) {
      // Get asset groups and asset trees scoped to a provided ID
      fetchAssets(scopeIds, CREATED_BY_SEEQ_WORKBENCH, { cancellationGroup }).then(({ data: { children } }) => {
        setScopedAssetGroups(children ?? []);
      });
      fetchAssets(scopeIds, undefined, { cancellationGroup }).then(({ data: { children } }) => {
        setScopedAssetTrees(children?.filter((item) => item?.scopedTo && scopeIds.includes(item.scopedTo)) ?? []);
      });
    }
    return () => {
      lastModalId = modalId;
    };
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isHierarchyNodeModal = itemTypeToShowInOverview === 'hierarchy-nodes' && pane === 'modal';
  const isScalingTableModal = itemTypeToShowInOverview === 'scaling-tables' && pane === 'modal';

  const isDoneLoadingResults = () => !searching && !isPaginating;
  const hasNoResults = () => isDoneLoadingResults() && items.length === 0 && currentItems.length === 0;
  const showErrorInScopedResults = () =>
    showOnlyScopedAssets
      ? isDoneLoadingResults() && scopedAssetGroups.length === 0 && scopedAssetTrees.length === 0 && mode === 'search'
      : true;

  const itemTableSortFunction = sortByKeyValuesFirst<SelectedSearchItem>('id', [itemTableId]);
  const scalingTableSortFunction = sortByKeyValuesFirst<SelectedSearchItem>('id', [scalingTableId]);

  const exploreAsset = (asset: SelectedSearchItem) => {
    return exploreAssetSearchActions(pane, asset.id, searchTypes, restrictExploration, scopeIds, excludeGloballyScoped);
  };

  const isRestrictedBreadcrumb = (breadcrumb: SearchBreadcrumb) =>
    isHierarchyNodeModal &&
    (breadcrumb.id === HOME_BREADCRUMB.id || breadcrumb.type === SeeqNames.Types.TableDefinition);

  const exploreScalingTable = async (item: SelectedSearchItem) => {
    setNameFilter('');
    return exploreScalingTableLevel(pane, {
      item,
      excludeGloballyScoped,
      scopeIds,
      searchTypes,
      workbooksFilter,
      searchLocallyScoped,
      mode,
      filterByWorkbook,
      setScalingTableIsCsv,
    });
  };

  const exploreHierarchy = async (item: SelectedSearchItem) => {
    setNameFilter('');
    return exploreHierarchyLevel(pane, {
      item,
      excludeGloballyScoped,
      scopeIds,
      searchTypes,
      workbooksFilter,
      searchLocallyScoped,
      mode,
      filterByWorkbook,
    });
  };

  const addHierarchyItemToTrend = async (item: SelectedSearchItem) => {
    const trendItem = {
      itemType: item.node?.itemMetadata ? API_TYPES_TO_ITEM_TYPES[item.node.itemMetadata.type!] : undefined,
      ...item,
    };
    const props = getHierarchyItemTrendProps(item, currentHierarchyPath);
    await addTrendItem(trendItem, props);
    doTrack(TRACK.TREE_BUILDER, 'Add item to trend', { type: trendItem.itemType });
  };

  /** used when clicking on items in the search widget */
  const viewHierarchiesLayer = async (item: SelectedSearchItem, isSearch = false) => {
    if (item.type === SeeqNames.Types.TableDefinition) {
      setNameFilter('');
      await setUpHierarchiesSearchForItemTable(pane, item, isSearch);
    } else if (item.type === SeeqNames.Types.Hierarchy) {
      if (!restrictExploration) {
        setNameFilter('');
        await fetchHierarchyLayer(pane, item, item.id, currentHierarchyPath, isSearch);
      }
    } else if (selectedHierarchy && !item.node?.leaf) {
      setNameFilter('');
      await fetchHierarchyLayer(pane, item, selectedHierarchy.id, currentHierarchyPath, isSearch);
    } else if (item.node?.leaf && pane === 'main') {
      // If the node is a leaf, we can just view the item
      await addHierarchyItemToTrend(item);
    }
  };

  const fetchUsages = (item: SelectedSearchItem) => {
    if (item.id) {
      return fetchItemUsages(item, pane, workbookId ? [workbookId] : [], false);
    } else {
      setNameFilter('');
      return exploreAssetSearchActions(
        pane,
        item.id,
        searchTypes,
        restrictExploration,
        undefined,
        excludeGloballyScoped,
      );
    }
  };

  const isScalingTable = (item: any): boolean => {
    return getScalingTableTypes().includes(item.includedProperties?.[SeeqNames.Properties.TableType]?.value);
  };

  const isScalingTableItem = (item: any): boolean => {
    return item.ancestors?.some((ancestor: any) => ancestor.type === SeeqNames.Types.TableDefinition) ?? false;
  };

  const isItemTable = (item: any): boolean => {
    return getItemTableTypes().includes(item.includedProperties?.[SeeqNames.Properties.TableType]?.value);
  };

  const isScalingCsvTable = (item: any) => {
    return item.includedProperties?.[SeeqNames.Properties.TableType]?.value === TableTypeEnum.SCALINGCSV;
  };

  const viewItem = (item: SelectedSearchItem | ItemSearchPreviewV1) => {
    const isInValidEditorState = !assetGroupEditorVisible || !scalingTableEditorVisible || pane !== 'main';

    if (isItemTable(item) || isHierarchy(item) || isHierarchyNode(item)) {
      viewHierarchiesLayer(item, false);
    } else if (isScalingTable(item) || isScalingTableItem(item)) {
      viewScalingTableLayer(item, true);
    } else if (isAsset(item) || isDatafile(item) || item.hasActiveChildren) {
      exploreAsset(item);
    } else if (isInValidEditorState) {
      const modelProps = getModelItemTrendProps(item);
      if (modelProps.instanceId) {
        addItemFromModelApplicationToTrend(item, modelProps);
      } else {
        onItemClick(item, mode, pane);
      }
    }
  };

  const addScalingTableColumnItem = async (item: SelectedSearchItem) => {
    const trendItem = {
      itemType: API_TYPES_TO_ITEM_TYPES[item.type!],
      ...item,
    };
    const props = await getScalingTableItemTrendProps(item);
    await addTrendItem(trendItem, props);
    doTrack(TRACK.SCALING_TABLE_CONFIGURATION, 'Add item to trend', { type: trendItem.itemType });
  };

  const addItemFromModelApplicationToTrend = async (
    item: SelectedSearchItem | ItemSearchPreviewV1,
    modelProps: ModelItemTrendProps,
  ): Promise<void> => {
    const trendItem = {
      itemType: API_TYPES_TO_ITEM_TYPES[item.type!],
      ...item,
    };
    await addTrendItem(trendItem, modelProps);
  };

  const viewScalingTableLayer = async (item: SelectedSearchItem, isSearch = false) => {
    if (item.type === SeeqNames.Types.TableDefinition || item.hasActiveChildren === true) {
      setNameFilter('');
      // In scaling table selection modal, don't drill into columns - just keep showing tables
      if (!isScalingTableModal) {
        await fetchScalingTableSearchItems(pane, item, isSearch);
      }
    } else {
      let scalingTableIsCSVTable = scalingTableIsCsv;
      if (!scalingTableIsCsv) {
        scalingTableIsCSVTable = isScalingCsvTable(item);
        setScalingTableIsCsv(scalingTableIsCSVTable);
      }
      await addScalingTableColumnItem(item);
    }
  };

  const fetchNextPageSearchResults = async () => {
    setIsPaginating();

    if (mode === 'scaling-tables-tree') {
      const currentItem = selectedScalingTableRow ?? selectedScalingTable;
      return currentItem ? fetchScalingTableSearchItems(pane, currentItem, true) : { items: [] };
    }

    if (mode === 'overview' || mode === 'tree') {
      return fetchAssetResults(pane, searchTypes, restrictExploration, scopeIds, excludeGloballyScoped);
    }

    return fetchItemResults(pane, searchTypes, scopeIds, excludeGloballyScoped);
  };

  const handleLoadMore = async () => {
    if (!searching && !isPaginating && hasNextPage) {
      const { items: newItems } = await fetchNextPageSearchResults();

      if (assetGroupEditorVisible || scalingTableEditorVisible) {
        setSelectAll(undefined);
      } else {
        const hasLeafItem = !!newItems?.find((item) => !(item.hasActiveChildren || isAsset(item)));
        hasLeafItem && setSelectAll(undefined);
      }
    }
  };

  function defaultSearchResultIcons(item: SelectedSearchItem) {
    let onItemClickForDefaultSearchResultIcons = onItemClick;
    if (showScalingTableItems) {
      onItemClickForDefaultSearchResultIcons = addScalingTableColumnItem;
    } else if (showHierarchyNodes) {
      onItemClickForDefaultSearchResultIcons = addHierarchyItemToTrend;
    }
    return Promise.resolve(
      <>
        {allowAssetReplacement && <SearchResultAssetIcons item={item} isAssetGroupView={assetGroupEditorVisible} />}
        <SearchResultIcons
          item={item}
          pinned={pinned}
          workbookDisplay={workbookDisplay}
          workbookId={workbookId}
          onItemClick={onItemClickForDefaultSearchResultIcons}
          allowAssetReplacement={allowAssetReplacement}
          restrictExploration={restrictExploration}
        />
      </>,
    );
  }

  const showScalingTableItems = mode === 'scaling-tables-tree';
  // Show the flat/tree toggle only at the table root level (not drilled into a group or row)
  const showScalingTableViewModeToggle =
    showScalingTableItems &&
    !!selectedScalingTable &&
    breadcrumbs[breadcrumbs.length - 1]?.type === SeeqNames.Types.TableDefinition;

  // In pinned, recently accessed or asset group mode, users can filter by the name, pulling this out of the
  // <SearchFilters/> component allows us to not have to rerender every time a user types in an input (CRAB-24095)
  const renderTemplateSearchGroup = (
    templateItems: SelectedSearchItem[],
    {
      loadStatus,
      label,
      onErrorClick,
      withMarginBottom = false,
      onSetModeIconClick,
    }: {
      loadStatus: SearchGroupProps['loadStatus'];
      label: string;
      onErrorClick: () => void;
      withMarginBottom?: boolean;
      onSetModeIconClick?: () => void;
    },
  ) => {
    const searchGroup = (
      <SearchGroup
        isSelectingAsset={isSelectingAsset}
        searchMode="models"
        searchTypes={searchTypes}
        scopeIds={scopeIds}
        restrictExploration={restrictExploration}
        pane={pane}
        searchResultIcons={searchResultIcons}
        mode={mode}
        indicateSelection={indicateSelection}
        selectedItemId={selectedItemId}
        hideCreateButton
        loadStatus={loadStatus}
        label={label}
        onErrorClick={onErrorClick}
        onClickItem={onSelectItem}
        items={limitAndFilterItems(templateItems, mode, nameFilter, restrictExploration, searchTypes)}
        totalItemsCount={filterItems(templateItems, mode, nameFilter, restrictExploration, searchTypes)?.length}
        onSetModeIconClick={onSetModeIconClick}
      />
    );

    return withMarginBottom ? <div className="tw:mb-sq-15">{searchGroup}</div> : searchGroup;
  };

  const renderOnlyNameFilter = (
    <div className="card card-default tw:mt-sq-5">
      <div className="tw:min-h-0.5 tw:p-4" data-testid="searchPanel">
        <TextField
          extraClassNames="tw:mb-sq-10 tw:w-full"
          id="searchInput"
          name="nameFilter"
          testId="nameOnlyFilter"
          placeholder={t('SEARCH_DATA.NAME_CONTAINS')}
          onChange={(name) => setNameFilter(name.target.value)}
          value={nameFilter}
        />
        <Button
          label={t('SEARCH_DATA.RESET')}
          onClick={() => {
            clear(
              pane,
              searchTypes,
              restrictExploration,
              scopeIds,
              excludeGloballyScoped,
              initialItemTable,
              initialHierarchy,
              initialScalingTable,
            );
            setNameFilter('');
            setScalingTableIsCsv(false);
          }}
          testId="resetButton"
        />
      </div>
    </div>
  );

  const isSearchingTreeBuilderTypesAndNotAtRootOfSearch =
    [SeeqNames.Types.Hierarchy, ITEM_TABLE_TYPE].some((type) => searchTypes.includes(type)) &&
    mode === 'search' &&
    (selectedItemTable || selectedHierarchy);

  const isSearchingScalingTableAndNotAtRootOfSearch =
    searchTypes.includes(SCALING_TABLE_TYPE) && mode === 'search' && (selectedScalingTable || selectedScalingTableRow);
  const showSearchFilters =
    _.includes(['tree', 'overview', 'search'], mode) &&
    !isSearchingTreeBuilderTypesAndNotAtRootOfSearch &&
    !isSearchingScalingTableAndNotAtRootOfSearch;
  const showHierarchyNodes = mode === 'hierarchy-nodes';
  const showItemHierarchies = mode === 'item-hierarchies';
  const showUsages = mode === 'usages';
  const showAssetTrees =
    mode !== 'overview' &&
    !showUsages &&
    !showScalingTableItems &&
    !showItemHierarchies &&
    !showHierarchyNodes &&
    !isSearchingTreeBuilderTypesAndNotAtRootOfSearch &&
    !isSearchingScalingTableAndNotAtRootOfSearch;
  const showSpinner = !isDoneLoadingResults();
  const showError =
    hasNoResults() && showErrorInScopedResults() && !currentAsset && !showUsages && !showItemHierarchies;
  const showUsagesError = hasNoResults() && showUsages;
  const showPinnedItems = (!showOnlyResults && !showUsages) || showPinnedAssets;
  const showAssetGroups =
    hasAssetGroupScope &&
    !showUsages &&
    !showScalingTableItems &&
    !showItemHierarchies &&
    !showHierarchyNodes &&
    !itemTypeToShowInOverview;
  const showScalingTables =
    !showUsages &&
    isPanelSearch &&
    (isScalingTableEditorEnabled() || hasScalingTableCapability()) &&
    !showScalingTableItems &&
    (hasScalingLicense || mode === 'overview');
  const showItemTables =
    !showUsages &&
    isPanelSearch &&
    isTreeBuilderEnabledForUser() &&
    !showScalingTableItems &&
    !showItemHierarchies &&
    !showHierarchyNodes &&
    (hasTreeBuilderLicense || mode === 'overview');
  const showRecentlyAccessed = !showOnlyResults && !showUsages && !showScalingTableItems;
  const showScalingTableItemSelection = showScalingTableItems && breadcrumbs.length === 2;

  const showInfinite = !_.includes(
    ['pinned', 'recent', 'asset-groups', 'usages', 'scaling-tables', 'item-tables', 'overview', 'item-hierarchies'],
    mode,
  );

  const getScopedAssetTrees = () => scopedAssetTrees.filter((item) => !isAssetGroup(item));
  const showErrorNoResults =
    (isDoneLoadingResults() &&
      !hasNoResults() &&
      currentItems.filter((item) => filterByName(item, mode, nameFilter)).length === 0) ||
    (hasNoResults() && currentAsset) ||
    (getScopedAssetTrees().length === 0 && mode === 'overview' && showOnlyScopedAssets);
  const placeholderCategoryTranslated = placeholderCategory ? t(placeholderCategory) : '';

  const areAllItemsSelected = areAllSelected(currentItems);

  let viewItemForCurrentItems: (item: SelectedSearchItem) => void = viewItem;
  if (showScalingTableItems) {
    viewItemForCurrentItems = viewScalingTableLayer;
  } else if (showItemHierarchies || showHierarchyNodes) {
    viewItemForCurrentItems = viewHierarchiesLayer;
  }
  const currentItemsAsSearchResults = () => {
    const potentiallyFilteredItems =
      showScalingTableItems || showHierarchyNodes
        ? limitAndFilterItems(currentItems, mode, nameFilter, restrictExploration, searchTypes)
        : currentItems;
    return _.map(potentiallyFilteredItems, (item) => (
      <SearchResult
        isSelectingAsset={isSelectingAsset}
        searchTypes={predefinedSearchTypes}
        customIconClasses={iconClasses}
        key={item.id}
        items={currentItems}
        searchResultIcons={searchResultIcons}
        item={item}
        onClickItem={() => viewItemForCurrentItems(item)}
        isSelected={indicateSelection && selectedItemId === item.id}
        itemSelected={_.includes(selectedItemIds, item.id)}
        showItemSelection={!showOverviewMode && pane !== 'modal' && !showScalingTableItemSelection}
        isAllUnselectable={isAllUnselectable}
        highlightItem={highlightItemId === item.id}
        onSelectItem={onSelectItem}
        assetGroupEditorVisible={assetGroupEditorVisible}
        showOnlyConditions={
          showOnlyConditions && !(mode === 'overview' && itemTypeToShowInOverview === 'scaling-tables')
        }
        doNotDisableOnEdit={doNotDisableOnEdit}
        noNavigate={restrictExploration}
      />
    ));
  };

  return (
    <div
      className="searchWidget tw:flex tw:h-full tw:flex-auto tw:flex-col tw:overflow-hidden"
      data-testid="searchWidget">
      {showSearchFilters ? (
        <SearchFilters
          pane={pane}
          searchTypes={searchTypes}
          scopeIds={scopeIds}
          excludeGloballyScoped={excludeGloballyScoped}
          filterByWorkbook={filterByWorkbook}
        />
      ) : (
        renderOnlyNameFilter
      )}
      {showAssetTrees && (
        <ul
          data-testid="searchBreadcrumbsAssetTrees"
          className="searchBreadcrumbs heading tw:mb-0 tw:flex tw:flex-none tw:list-none! tw:flex-row tw:flex-wrap tw:pl-0">
          {breadcrumbs.map((breadcrumb, index: number) => {
            const isViewMode = breadcrumb.type === 'VIEW_MODE';
            const isLast = breadcrumbs.length - 1 === index;
            return (
              <li
                data-testid="searchBreadcrumbAssetTrees"
                tabIndex={isLast ? undefined : 0}
                onKeyUp={(e) =>
                  e.keyCode === KEY_CODES.ENTER && !isLast && !breadcrumb.isLoading && exploreAsset(breadcrumb)
                }
                onClick={() => !breadcrumb.isLoading && !isLast && exploreAsset(breadcrumb)}
                key={breadcrumb.id}>
                {breadcrumb.isLoading && <IconWithSpinner spinning={true} />}
                {!breadcrumb.id && (
                  <Icon icon={faHouseChimney} extraClassNames="tw:pl-sq-5" testId="breadcrumbHomeIcon" />
                )}
                <span className="tw:text-sq-15">{isViewMode ? t(breadcrumb.name) : breadcrumb.name}</span>
                {!breadcrumb.isLoading && breadcrumbs.length === 1 && (
                  <span className="tw:pl-sq-5 tw:text-sq-15">{t('SEARCH_DATA.ASSET_TREES')}</span>
                )}
              </li>
            );
          })}
        </ul>
      )}
      {(showScalingTableItems || !!selectedScalingTable || !!selectedScalingTableRow) && breadcrumbs && (
        <div className="heading tw:mb-0 tw:flex tw:flex-row tw:items-center tw:justify-between">
          <ul
            data-testid="searchBreadcrumbsScalingTable"
            className="searchBreadcrumbs tw:my-0 tw:flex tw:min-w-0 tw:flex-initial tw:list-none! tw:flex-row tw:flex-wrap tw:pl-0">
            {breadcrumbs.map((breadcrumb, index: number) => {
              const isViewMode = breadcrumb.type === 'VIEW_MODE';
              const isLast = breadcrumbs.length - 1 === index;
              return (
                <li
                  data-testid="searchBreadcrumbScalingTable"
                  tabIndex={isLast ? undefined : 0}
                  onKeyUp={(e) =>
                    e.keyCode === KEY_CODES.ENTER && !isLast && !breadcrumb.isLoading && exploreScalingTable(breadcrumb)
                  }
                  onClick={() => !breadcrumb.isLoading && !isLast && exploreScalingTable(breadcrumb)}
                  key={breadcrumb.id}>
                  {breadcrumb.isLoading && <IconWithSpinner spinning={true} />}
                  {!breadcrumb.id && (
                    <Icon icon={faHouseChimney} extraClassNames="tw:pl-sq-5" testId="breadcrumbHomeIcon" />
                  )}
                  <span className="tw:text-sq-15">{isViewMode ? t(breadcrumb.name) : breadcrumb.name}</span>
                </li>
              );
            })}
          </ul>
          {showScalingTableViewModeToggle && (
            <ToggleSwitch
              testId="scalingTableViewModeToggle"
              isOn={scalingTableViewMode === 'tree'}
              onChange={() => {
                const nextMode = scalingTableViewMode === 'flat' ? 'tree' : 'flat';
                setScalingTableViewMode(nextMode);
                if (selectedScalingTable) {
                  void fetchScalingTableSearchItems(pane, selectedScalingTable, false);
                }
              }}
              formattedLabel={t('SEARCH_DATA.SCALING_TABLE_EXPAND')}
            />
          )}
        </div>
      )}
      {(showItemHierarchies || showHierarchyNodes || !!selectedHierarchy || !!selectedItemTable) && breadcrumbs && (
        <ul
          data-testid="searchBreadcrumbsItemHierarchies"
          className="searchBreadcrumbs heading tw:mb-0 tw:flex tw:flex-none tw:list-none! tw:flex-row tw:flex-wrap tw:pl-0">
          {breadcrumbs.map((breadcrumb, index: number) => {
            const isViewMode = breadcrumb.type === 'VIEW_MODE';
            const isLast = breadcrumbs.length - 1 === index;
            const isRestricted = isRestrictedBreadcrumb(breadcrumb);
            return (
              <li
                className={clsx(isRestricted && 'restricted')}
                data-testid={`searchBreadcrumbItemHierarchies_${breadcrumb.name}`}
                tabIndex={isLast || isRestricted ? undefined : 0}
                onKeyUp={(e) =>
                  e.keyCode === KEY_CODES.ENTER &&
                  !isLast &&
                  !breadcrumb.isLoading &&
                  !isRestricted &&
                  exploreHierarchy(breadcrumb)
                }
                onClick={() => !breadcrumb.isLoading && !isLast && !isRestricted && exploreHierarchy(breadcrumb)}
                key={breadcrumb.id}>
                {breadcrumb.isLoading && <IconWithSpinner spinning={true} />}
                {!breadcrumb.id && (
                  <Icon
                    icon={faHouseChimney}
                    extraClassNames="tw:pl-sq-5"
                    testId="breadcrumbHomeIcon"
                    type={isRestricted ? 'dark-gray' : undefined}
                  />
                )}
                <span className="tw:text-sq-15">{isViewMode ? t(breadcrumb.name) : breadcrumb.name}</span>
              </li>
            );
          })}
        </ul>
      )}
      {showError && <ErrorWithBackground errorText="SEARCH_DATA.NO_RESULTS" dismissible={false} type="Warning" />}
      {showInfinite &&
      !showOverviewMode &&
      !showErrorNoResults &&
      pane !== 'modal' &&
      !_.isEmpty(currentItems) &&
      !showScalingTableItemSelection ? (
        <div className="itemRow tw:pt-sq-10 tw:pb-sq-10 tw:pl-sq-5">
          <TrendMultiSelect items={currentItems} />
        </div>
      ) : null}
      {showAssetTrees && selectAllCallback !== undefined && !filterByWorkbook && (
        <div className="itemRow tw:pt-sq-10 tw:pb-sq-10">
          <Checkbox
            checked={areAllItemsSelected}
            label={t(areAllItemsSelected ? 'UNSELECT_ALL' : 'SELECT_ALL')}
            onClick={() => selectAllCallback(currentItems)}
          />
        </div>
      )}
      <div
        id="spec-search-results"
        data-testid="spec-search-results"
        className={clsx('tw:flex tw:h-full tw:flex-col tw:overflow-auto', searchResultsClassNames)}
        ref={scrollParentRef}>
        {showUsages && (
          <>
            <ul
              data-testid="searchBreadcrumbsUsages"
              className="searchBreadcrumbs heading tw:mb-0 tw:flex tw:flex-none tw:list-none! tw:flex-row tw:flex-wrap tw:pl-0">
              {_.map(breadcrumbs, (breadcrumb, index) => {
                const isTitle = breadcrumb.id === 'ITEM_USAGES';
                const isItemOrHomeBreadcrumb = !(breadcrumbs.length - 1 === _.toNumber(index) || isTitle);
                return (
                  <li
                    data-testid="searchBreadcrumbUsages"
                    tabIndex={!isItemOrHomeBreadcrumb ? undefined : 0}
                    onKeyUp={(e) =>
                      e.keyCode === 13 && isItemOrHomeBreadcrumb && !breadcrumb.isLoading && fetchUsages(breadcrumb)
                    }
                    onClick={() => {
                      !breadcrumb.isLoading && isItemOrHomeBreadcrumb && fetchUsages(breadcrumb);
                    }}
                    key={breadcrumb.id}>
                    {breadcrumb.isLoading && <IconWithSpinner spinning={true} />}
                    {!breadcrumb.id && <Icon icon={faHouseChimney} testId="usagesHomeIcon" />}
                    {isTitle && <Icon icon={fcUsage} extraClassNames="sq-icon-dark-gray tw:pr-sq-5 tw:pl-sq-5" />}
                    <span className={isTitle ? 'title tw:text-sq-15 tw:font-bold' : 'tw:text-sq-15'}>
                      {t(breadcrumb.name)}{' '}
                    </span>
                  </li>
                );
              })}
            </ul>
            <SearchGroup
              isSelectingAsset={isSelectingAsset}
              predefinedSearchTypes={predefinedSearchTypes}
              loadStatus={searching as SearchGroupProps['loadStatus']}
              label="USAGES"
              searchMode="usages"
              searchTypes={[]}
              scopeIds={scopeIds}
              restrictExploration={restrictExploration}
              pane={pane}
              onErrorClick={_.noop}
              onClickItem={viewItem}
              searchResultIcons={searchResultIcons}
              mode={mode}
              items={limitAndFilterItems(items, mode, nameFilter, restrictExploration, searchTypes)}
              indicateSelection={indicateSelection}
              selectedItemId={selectedItemId}
            />
            {showSpinner && (
              <div className="tw:flex tw:flex-none tw:flex-row tw:items-center tw:justify-center tw:pt-sq-10 tw:pb-sq-10">
                <IconWithSpinner spinning={true} large={true} />
              </div>
            )}
          </>
        )}

        {showUsagesError && (
          <ErrorWithBackground errorText="SEARCH_DATA.NO_RESULTS" dismissible={false} type="Warning" />
        )}

        {showPinnedItems && (
          <SearchGroup
            isSelectingAsset={isSelectingAsset}
            predefinedSearchTypes={predefinedSearchTypes}
            loadStatus={pinnedLoadStatus}
            label="PINNED"
            pane={pane}
            searchMode="pinned"
            searchTypes={searchTypes}
            scopeIds={scopeIds}
            restrictExploration={restrictExploration}
            onErrorClick={() => fetchPinnedItems(workbookId)}
            onClickItem={(item) => {
              if (showScalingTableItems || isScalingTable(item)) {
                viewScalingTableLayer(item);
              } else if (showItemHierarchies || isItemTable(item) || isHierarchy(item)) {
                viewHierarchiesLayer(item);
              } else {
                viewItem(item);
              }
            }}
            searchResultIcons={searchResultIcons}
            mode={mode}
            items={limitAndFilterItems(
              !showPinnedAssets ? pinned : filterByAssetItemType(pinned),
              mode,
              nameFilter,
              restrictExploration,
              searchTypes,
            )}
            indicateSelection={indicateSelection}
            selectedItemId={selectedItemId}
            totalItemsCount={
              filterItems(
                !showPinnedAssets ? pinned : filterByAssetItemType(pinned),
                mode,
                nameFilter,
                restrictExploration,
                searchTypes,
              ).length
            }
          />
        )}

        {showAssetGroups && (
          <SearchGroup
            isSelectingAsset={isSelectingAsset}
            predefinedSearchTypes={predefinedSearchTypes}
            loadStatus={assetGroupsLoadStatus}
            label="ASSET_GROUPS"
            searchMode="asset-groups"
            searchTypes={searchTypes}
            scopeIds={scopeIds}
            restrictExploration={restrictExploration}
            onErrorClick={() => fetchAssetGroups(scopeIds)}
            pane={pane}
            onClickItem={viewItem}
            searchResultIcons={searchResultIcons}
            mode={mode}
            noItemsMessage={
              hasAssetGroups && sqWorkbookStore.workbookDisplay === WORKBOOK_DISPLAY.EDIT
                ? 'SEARCH_DATA.NO_ASSET_GROUPS'
                : undefined
            }
            items={limitAndFilterItems(
              showOnlyScopedAssets ? scopedAssetGroups : assetGroups,
              mode,
              nameFilter,
              restrictExploration,
              searchTypes,
            )}
            indicateSelection={indicateSelection}
            selectedItemId={selectedItemId}
            totalItemsCount={
              filterItems(
                showOnlyScopedAssets ? scopedAssetGroups : assetGroups,
                mode,
                nameFilter,
                restrictExploration,
                searchTypes,
              ).length
            }
          />
        )}

        {showItemTables && (
          <SearchGroup
            isSelectingAsset={isSelectingAsset}
            predefinedSearchTypes={predefinedSearchTypes}
            loadStatus={itemTablesLoadStatus}
            label="ITEM_TABLES"
            searchMode="item-tables"
            searchTypes={searchTypes}
            scopeIds={scopeIds}
            restrictExploration={restrictExploration}
            onErrorClick={() => fetchItemTables([workbookId])}
            pane={pane}
            onClickItem={async (params) => {
              await viewHierarchiesLayer(params);
            }}
            searchResultIcons={searchResultIcons}
            mode={mode}
            noItemsMessage={
              sqWorkbookStore.workbookDisplay === WORKBOOK_DISPLAY.EDIT ? 'SEARCH_DATA.NO_ITEM_TABLE' : undefined
            }
            items={limitFilterAndSortItems(
              itemTables,
              mode,
              nameFilter,
              restrictExploration,
              searchTypes,
              itemTableSortFunction,
            )}
            indicateSelection={indicateSelection}
            disableSearchGroup={!hasTreeBuilderLicense && mode === 'overview'}
            tooltipLink={`${SUPPORT_LINK}/tree-builder-preview`}
            onTooltipClicked={() =>
              doTrack(TRACK.ENTERPRISE_LICENSING, 'Tree Builder Knowledge Base Clicked', {
                hasEnterpriseLicense: false,
              })
            }
            hideCreateButton={!hasTreeBuilderLicense}
            selectedItemId={selectedItemId}
            totalItemsCount={filterItems(itemTables, mode, nameFilter, restrictExploration, searchTypes)?.length}
            showNewBadge={!hasCreatedItemTable}
          />
        )}

        {showItemHierarchies && chosenItemTable && (
          <SearchGroup
            isSelectingAsset={isSelectingAsset}
            predefinedSearchTypes={predefinedSearchTypes}
            loadStatus={searching as any}
            label="HIERARCHY"
            searchMode="item-hierarchies"
            searchTypes={searchTypes}
            scopeIds={scopeIds}
            restrictExploration={restrictExploration}
            onErrorClick={async () => await viewHierarchiesLayer(chosenItemTable)}
            pane={pane}
            onClickItem={!restrictExploration ? viewHierarchiesLayer : _.noop}
            searchResultIcons={searchResultIcons}
            mode={mode}
            noItemsMessage={
              sqWorkbookStore.workbookDisplay === WORKBOOK_DISPLAY.EDIT ? 'SEARCH_DATA.NO_ITEM_HIERARCHIES' : undefined
            }
            items={limitAndFilterItems(currentItems, mode, nameFilter, restrictExploration, searchTypes)}
            indicateSelection={indicateSelection}
            selectedItemId={selectedItemId}
            totalItemsCount={filterItems(currentItems, mode, nameFilter, restrictExploration, searchTypes)?.length}
          />
        )}

        {showScalingTables && (
          <SearchGroup
            isSelectingAsset={isSelectingAsset}
            predefinedSearchTypes={predefinedSearchTypes}
            loadStatus={tableDefinitionsLoadStatus}
            label="SCALING"
            searchMode="scaling-tables"
            searchTypes={searchTypes}
            scopeIds={scopeIds}
            restrictExploration={restrictExploration}
            onErrorClick={() => fetchScalingTables([workbookId], sqWorkbookStore.isVantage)}
            pane={pane}
            onClickItem={viewScalingTableLayer}
            searchResultIcons={searchResultIcons}
            mode={mode}
            noItemsMessage={
              sqWorkbookStore.workbookDisplay === WORKBOOK_DISPLAY.EDIT ? 'SEARCH_DATA.NO_SCALING' : undefined
            }
            items={limitFilterAndSortItems(
              tableDefinitions,
              mode,
              nameFilter,
              restrictExploration,
              searchTypes,
              scalingTableSortFunction,
            )}
            indicateSelection={indicateSelection}
            selectedItemId={selectedItemId}
            totalItemsCount={filterItems(tableDefinitions, mode, nameFilter, restrictExploration, searchTypes)?.length}
            disableSearchGroup={!hasScalingLicense && mode === 'overview'}
            tooltipLink={`${SUPPORT_LINK}/scaling-tables-preview`}
            onTooltipClicked={() =>
              doTrack(TRACK.ENTERPRISE_LICENSING, 'Scaling TableKnowledge Base Clicked', {
                hasEnterpriseLicense: false,
              })
            }
            showNewBadge={!hasCreatedScalingTable}
          />
        )}

        {showRecentlyAccessed && (
          <SearchGroup
            isSelectingAsset={isSelectingAsset}
            loadStatus={recentlyAccessedLoadStatus}
            label="RECENTLY_ACCESSED"
            searchMode="recent"
            searchTypes={searchTypes}
            scopeIds={scopeIds}
            restrictExploration={restrictExploration}
            pane={pane}
            onErrorClick={() => fetchRecentlyAccessedItems(workbookId)}
            onClickItem={viewItem}
            searchResultIcons={searchResultIcons}
            mode={mode}
            items={limitAndFilterItems(recentlyAccessed, mode, nameFilter, restrictExploration, searchTypes)}
            indicateSelection={indicateSelection}
            selectedItemId={selectedItemId}
            totalItemsCount={filterItems(recentlyAccessed, mode, nameFilter, restrictExploration, searchTypes)?.length}
          />
        )}

        {itemTypeToShowInOverview === 'models' && expandedTemplatesSection && (
          <ul
            data-testid="searchBreadcrumbsTemplates"
            className="searchBreadcrumbs heading tw:mb-0 tw:flex tw:flex-none tw:list-none! tw:flex-row tw:flex-wrap tw:pl-0">
            <li
              data-testid="searchBreadcrumbTemplates"
              className="tw:cursor-pointer"
              tabIndex={0}
              onKeyUp={(e) => e.keyCode === KEY_CODES.ENTER && setExpandedTemplatesSection(null)}
              onClick={() => setExpandedTemplatesSection(null)}>
              <Icon icon={faHouseChimney} extraClassNames="tw:pl-sq-5" testId="breadcrumbHomeIcon" />
              <span className="tw:pl-sq-5 tw:text-sq-15">
                {expandedTemplatesSection === TEMPLATE_SECTION_TYPES.MY && t('SEARCH_DATA.TEMPLATES.MINE')}
                {expandedTemplatesSection === TEMPLATE_SECTION_TYPES.SHARED &&
                  t('SEARCH_DATA.TEMPLATES.SHARED_TEMPLATES')}
                {expandedTemplatesSection === TEMPLATE_SECTION_TYPES.INDUSTRY &&
                  t('SEARCH_DATA.TEMPLATES.INDUSTRY_TEMPLATES')}
              </span>
            </li>
          </ul>
        )}

        {itemTypeToShowInOverview === 'models' && showOverviewMode && !expandedTemplatesSection && (
          <>
            {(filteredModelItems.length > 0 || templatesLoadStatus !== LOAD_STATUS.LOADED) &&
              renderTemplateSearchGroup(filteredModelItems, {
                loadStatus: templatesLoadStatus,
                label: 'TEMPLATES.MINE',
                onErrorClick: () => workbookId && fetchTemplates([workbookId]),
                withMarginBottom: true,
                onSetModeIconClick: () => setExpandedTemplatesSection(TEMPLATE_SECTION_TYPES.MY),
              })}
            {(sharedModelItems.length > 0 || sharedTemplatesLoadingStatus !== LOAD_STATUS.LOADED) &&
              renderTemplateSearchGroup(sharedModelItems, {
                loadStatus: sharedTemplatesLoadingStatus,
                label: 'TEMPLATES.SHARED_TEMPLATES',
                onErrorClick: () => workbookId && fetchSharedTemplates(workbookId),
                withMarginBottom: true,
                onSetModeIconClick: () => setExpandedTemplatesSection(TEMPLATE_SECTION_TYPES.SHARED),
              })}
            {(industryModelItems.length > 0 || sharedTemplatesLoadingStatus !== LOAD_STATUS.LOADED) &&
              renderTemplateSearchGroup(industryModelItems, {
                loadStatus: sharedTemplatesLoadingStatus,
                label: 'TEMPLATES.INDUSTRY_TEMPLATES',
                onErrorClick: () => workbookId && fetchSharedTemplates(workbookId),
                onSetModeIconClick: () => setExpandedTemplatesSection(TEMPLATE_SECTION_TYPES.INDUSTRY),
              })}
          </>
        )}

        {itemTypeToShowInOverview === 'models' &&
          expandedTemplatesSection &&
          (() => {
            const getTemplateItemsForSection = (section: TemplateSectionType): SelectedSearchItem[] => {
              switch (section) {
                case TEMPLATE_SECTION_TYPES.MY:
                  return filteredModelItems;
                case TEMPLATE_SECTION_TYPES.SHARED:
                  return sharedModelItems;
                case TEMPLATE_SECTION_TYPES.INDUSTRY:
                  return industryModelItems;
              }
            };

            const templateItems = getTemplateItemsForSection(expandedTemplatesSection);

            return (
              <>
                {filterItems(templateItems, 'models', nameFilter, restrictExploration, searchTypes).map((item) => (
                  <SearchResult
                    isSelectingAsset={isSelectingAsset}
                    searchTypes={searchTypes}
                    customIconClasses={iconClasses}
                    key={item.id}
                    items={templateItems}
                    searchResultIcons={searchResultIcons}
                    item={item}
                    onClickItem={() => onSelectItem(item)}
                    itemSelected={false}
                    showItemSelection={false}
                    highlightItem={false}
                    onSelectItem={onSelectItem}
                    noNavigate={restrictExploration}
                  />
                ))}
              </>
            );
          })()}

        {showOverviewMode && itemTypeToShowInOverview !== 'models' && (
          <>
            <div
              data-testid="overviewHeading"
              className="heading tw:mb-0 tw:ml-0.5 tw:flex tw:flex-row tw:justify-between">
              <div className="text-interactive tw:text-sq-15">{t(overviewLabel ?? 'SEARCH_DATA.ASSET_TREES')}</div>
              {!itemTypeToShowInOverview && (
                <DatasourcePreferencesToggle
                  on={isUsingDatasourcePrefsAssetTrees}
                  onChange={() => setIsUsingDatasourcePrefsAssetTrees(!isUsingDatasourcePrefsAssetTrees)}
                  formattedLabel={t('SEARCH_DATA.USE_DATASOURCE_PREFERENCES')}
                  onClick={() => {
                    setUserPreferencesTab(PREFERENCE_TABS.DATASOURCES);
                    setUserPreferencesDisplay(true);
                  }}
                  extraClassNames="tw:mr-sq-5"
                />
              )}
            </div>
            <div>{currentItemsAsSearchResults()}</div>
          </>
        )}

        {showInfinite && (
          <>
            <InfiniteScroll
              pageStart={0}
              hasMore={hasNextPage}
              loadMore={handleLoadMore}
              useWindow={false}
              initialLoad={false}
              getScrollParent={() => scrollParentRef.current}>
              <>{currentItemsAsSearchResults()}</>
            </InfiniteScroll>

            {showSpinner && (
              <div className="tw:flex tw:flex-none tw:flex-row tw:items-center tw:justify-center tw:pt-sq-10 tw:pb-sq-10">
                <IconWithSpinner testId="loadingResults" spinning={true} large={true} />
              </div>
            )}
          </>
        )}
      </div>
      {showErrorNoResults && !showUsages && (
        <ErrorWithBackground
          type="Warning"
          dismissible={false}
          errorText={
            showOnlyScopedAssets ? 'SEARCH_DATA.NO_RESULTS_IN_ANALYSIS' : 'SEARCH_DATA.NO_RESULTS_WITHIN_CATEGORY'
          }
          errorParameters={{ category: placeholderCategoryTranslated }}
        />
      )}
      {showCreateNewItem && (
        <div
          data-testid="createNewItemMessage"
          className="tw:mt-sq-20 tw:flex tw:flex-col tw:items-center tw:justify-center">
          <div className="tw:mb-sq-10 tw:font-bold">{t(createNewItemMessage)}</div>
          {onCreateNewItem && (
            <Button
              testId="createNewItemButton"
              label={t(createNewItemLabel)}
              onClick={onCreateNewItem}
              variant="theme"
            />
          )}
        </div>
      )}
    </div>
  );
};
